import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Play as PlayIcon, Pause as PauseIcon, StepForward as StepForwardIcon, StepBack as StepBackwardIcon } from 'lucide-react';

const PLAYBACK_RATES = [0.25, 0.5, 1, 2];

const formatTime = (seconds: number): string => {
    if (!seconds || !isFinite(seconds) || seconds < 0) {
        return '00:00';
    }
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${minutes}:${secs}`;
};

const SEEK_CLASS =
    'h-2 w-full min-w-0 flex-1 cursor-pointer appearance-none rounded-full [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md';

interface PlaybackControlsProps {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    playbackRate: number;
    onTogglePlay: () => void;
    onSeek: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFrameStep: (direction: number) => void;
    onRateChange: (rate: number) => void;
    /** Single-row layout (~50px tall) for narrow/short viewports; default is the full card. */
    compact?: boolean;
}

// Shared between the desktop and mobile layouts so scroll/visibility fixes
// cannot land in one breakpoint and miss the other.
const PlaybackControls: React.FC<PlaybackControlsProps> = ({
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    onTogglePlay,
    onSeek,
    onFrameStep,
    onRateChange,
    compact = false,
}) => {
    const progressPct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
    const seekStyle = {
        background: `linear-gradient(to right, hsl(var(--primary)) ${progressPct}%, hsl(var(--primary) / 0.25) ${progressPct}%)`,
    };

    const seekInput = (
        <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.01"
            value={currentTime}
            onChange={onSeek}
            aria-label="Seek video position"
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            className={SEEK_CLASS}
            style={seekStyle}
        />
    );

    if (compact) {
        const nextRate = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(playbackRate) + 1) % PLAYBACK_RATES.length];
        return (
            <div className="flex shrink-0 items-center gap-1.5 rounded-lg border bg-card px-2 py-1.5">
                <Button variant="ghost" size="icon" onClick={() => onFrameStep(-1)} className="h-8 w-8 shrink-0" title="Step Backward" aria-label="Step backward one frame">
                    <StepBackwardIcon />
                </Button>
                <Button variant="default" size="icon" onClick={onTogglePlay} className="h-9 w-9 shrink-0" title={isPlaying ? 'Pause' : 'Play'} aria-label={isPlaying ? 'Pause video' : 'Play video'} aria-pressed={isPlaying}>
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onFrameStep(1)} className="h-8 w-8 shrink-0" title="Step Forward" aria-label="Step forward one frame">
                    <StepForwardIcon />
                </Button>
                {seekInput}
                <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRateChange(nextRate)}
                    className="h-7 shrink-0 px-1.5 text-xs tabular-nums"
                    title="Playback speed (tap to change)"
                    aria-label={`Playback speed ${playbackRate}x, tap to change`}
                >
                    {playbackRate}x
                </Button>
            </div>
        );
    }

    return (
        <Card className="shrink-0">
            <CardContent className="pt-4 pb-4 space-y-3">
                <div className="space-y-2">
                    {seekInput}
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>
                <div className="flex justify-center items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onFrameStep(-1)}
                        className="h-9 w-9"
                        title="Step Backward"
                        aria-label="Step backward one frame"
                    >
                        <StepBackwardIcon />
                    </Button>
                    <Button
                        variant="default"
                        size="icon"
                        onClick={onTogglePlay}
                        className="h-10 w-10"
                        title={isPlaying ? "Pause" : "Play"}
                        aria-label={isPlaying ? "Pause video" : "Play video"}
                        aria-pressed={isPlaying}
                    >
                        {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onFrameStep(1)}
                        className="h-9 w-9"
                        title="Step Forward"
                        aria-label="Step forward one frame"
                    >
                        <StepForwardIcon />
                    </Button>
                    <div className="flex gap-1 ml-4">
                        {PLAYBACK_RATES.map(rate => (
                            <Button
                                key={rate}
                                variant={playbackRate === rate ? "default" : "outline"}
                                size="sm"
                                onClick={() => onRateChange(rate)}
                                className="px-2 h-7 text-xs"
                                aria-label={`Playback speed ${rate}x`}
                                aria-pressed={playbackRate === rate}
                            >
                                {rate}x
                            </Button>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default PlaybackControls;
